/*
 * The inspiration stack.
 *
 * The section pins, and the lines arrive one at a time from below, each locking
 * into its slot beneath the one before it. A line is tinted for as long as it is
 * travelling and snaps to its settled colour the moment it lands, so at any
 * point in the scroll exactly one line reads as "in motion".
 *
 * The first line is already in position when the pin begins — it is the anchor
 * the rest of the stack builds under.
 */
import { defineScene, reducedMotion, scenePriority } from '@theme/about-story';

defineScene('about-inspiration', (root, gsap) => {
	const lines = Array.from(root.querySelectorAll('[data-about-line]'));
	if (!lines.length) return;

	/* Unset colours fall back to the scheme's heading colour, which is where the
	   brand lilac lives — so the travelling line tracks the palette rather than a
	   literal copied into the section settings. */
	const styles = getComputedStyle(root);
	const themeHeading = styles.getPropertyValue('--color-foreground-heading').trim();
	const active = root.dataset.activeColor || themeHeading || '#ddceff';
	const settled = root.dataset.settledColor || '#ffffff';
	const perLine = Number(root.dataset.perLine) || 45;
	const enter = Number(root.dataset.enter) || 30;

	/* Two independent knobs, because scrub spreads the whole timeline across the
	   same pinned distance: shortening the gap between starts brings each line in
	   sooner, while lengthening its travel makes it move more slowly. Tied
	   together they would only trade one against the other. */
	const travel = Number(root.dataset.travel) || 2.2;
	const stagger = Number(root.dataset.stagger) || 0.7;
	const pinStart = Number(root.dataset.pinStart) || 20;

	/* Pinning takes the scrollbar away from the reader, which reduced motion
	   should never do. Show the finished stack instead. */
	if (reducedMotion()) {
		gsap.set(lines, { color: settled, clearProps: 'transform,opacity' });
		return;
	}

	gsap.set(lines[0], { color: settled });

	const timeline = gsap.timeline({
		scrollTrigger: {
			trigger: root,
			/* Engages while the section is still coming up the screen rather than
			   waiting for it to reach the top, so the stack is already building by
			   the time it is fully in view. Whatever sits above stays visible for
			   the length of the pin, which is what holds the wave in frame. */
			start: `top ${pinStart}%`,
			end: () => `+=${(lines.length - 1) * perLine}vh`,
			/* The stage, not <about-inspiration>: pinning moves the node it pins. */
			pin: root.querySelector(".about-inspiration__stage"),
			scrub: 1,
			anticipatePin: 1,
			invalidateOnRefresh: true,
			refreshPriority: scenePriority(root),
		},
	});

	lines.slice(1).forEach((line, index) => {
		/* `stagger` beats between starts, `travel` beats to arrive — the
		   difference between the two is how much the lines overlap. */
		const at = index * stagger;

		timeline
			.fromTo(
				line,
				{ y: () => (window.innerHeight * enter) / 100, autoAlpha: 0, color: active },
				{ y: 0, autoAlpha: 1, ease: 'none', duration: travel },
				at
			)
			/* Landing, not fading: the colour swap marks the end of the travel. */
			.set(line, { color: settled }, at + travel);
	});
});
